"use client";!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:zestlife-web":true});}catch(e){}}();
import React, { useCallback, useContext, useLayoutEffect, useRef } from "react"
import { ApolloLink } from "@apollo/client"
import {
  ApolloNextAppProvider,
  ApolloClient,
  SSRMultipartLink,
} from "@apollo/client-integration-nextjs"
import { LocalState } from "@apollo/client/local-state"
import { GraphQLWsLink } from "@apollo/client/link/subscriptions"
import { createClient } from "graphql-ws"
import { getMainDefinition } from "@apollo/client/utilities"
import { loadErrorMessages, loadDevMessages } from "@apollo/client/dev"
import { cache } from "../../graphql/cache"
import { AuthenticationRequiredError } from "../auth/clientSession"
import { ClientSessionContext } from "../auth/ClientSessionContext"
import { createAuthenticatedLink, createSessionLink } from "./authenticatedLink"
import { reportOperationError } from "../logging"

if (process.env.NEXT_PUBLIC_ENV !== "production") {
  loadDevMessages()
  loadErrorMessages()
}

export function ApolloWrapper({ children }: { children: React.ReactNode }) {
  const credentials = useContext(ClientSessionContext)
  const clientRef = useRef<ApolloClient | null>(null)
  if (!credentials) throw new Error("ApolloWrapper requires SessionManager")

  useLayoutEffect(
    () =>
      credentials.subscribe(() => {
        // Identity changes cancel requests before removing the previous account's data.
        void clientRef.current?.clearStore().catch((error) => {
          reportOperationError(error, { operation: "ClearSessionCache" })
        })
      }),
    [credentials],
  )

  const makeClient = useCallback(() => {
    const uri =
      process.env.NEXT_PUBLIC_API_GRAPHQL_END_POINT ||
      "http://localhost:3000/api/graphql"
    const http = createAuthenticatedLink({
      uri,
      session: credentials,
      reportError: reportOperationError,
    })
    let link = http

    if (typeof window !== "undefined") {
      const wsClient = createClient({
        url: uri.replace(/^http/, "ws"),
        connectionParams: async () => {
          const token = await credentials.getToken()
          if (!token) throw new AuthenticationRequiredError()
          return { authorization: `Bearer ${token}` }
        },
      })
      // Active subscriptions must not survive logout/account changes.
      credentials.subscribe(() => wsClient.terminate())
      link = ApolloLink.split(
        ({ query }) => {
          const definition = getMainDefinition(query)
          return (
            definition.kind === "OperationDefinition" &&
            definition.operation === "subscription"
          )
        },
        ApolloLink.from([
          createSessionLink(credentials, reportOperationError, () =>
            wsClient.terminate(),
          ),
          new GraphQLWsLink(wsClient),
        ]),
        http,
      )
    } else {
      link = ApolloLink.from([new SSRMultipartLink({ stripDefer: true }), http])
    }

    const client = new ApolloClient({ link, cache, localState: new LocalState({}) })
    clientRef.current = client
    return client
  }, [credentials])

  return (
    <ApolloNextAppProvider makeClient={makeClient}>{children}</ApolloNextAppProvider>
  )
}
